import { FilterIcon, GroupIcon } from "lucide-react";

import {
  FilterSubMenuMultiselect,
  Row,
  TableOrderByDropdown,
} from "@/components";
import {
  MyAviaryUserSelector,
  PendingSelection,
} from "@/components/my-aviary/MyAviaryUserSelector";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import {
  ADMIN_USER_IDS,
  myCandidaciesSortOptions,
  myCandidaciesStatusOptions,
} from "@/lib/constants";
import { MyCandidaciesGroupBy, MyCandidaciesQueryParams } from "@/types";

const groupByOptions = [
  { label: "Job", value: MyCandidaciesGroupBy.Job },
  { label: "Stage", value: MyCandidaciesGroupBy.Status },
];

export const MyAviaryTableQueryOptions = ({
  handleUpdateView,
  myCandidaciesQueryParams,
  userSelectorDisplayedQueryParams,
  userSelectorOpen,
  userSelectorPendingSelection,
  onUserSelectorOpenChange,
  onSelectAllUsers,
  onSelectUser,
}: {
  handleUpdateView: (
    myCandidaciesQueryParams: MyCandidaciesQueryParams,
  ) => void;
  myCandidaciesQueryParams: MyCandidaciesQueryParams;
  userSelectorDisplayedQueryParams: MyCandidaciesQueryParams;
  userSelectorOpen: boolean;
  userSelectorPendingSelection: PendingSelection | null;
  onUserSelectorOpenChange: (open: boolean) => void;
  onSelectAllUsers: () => void;
  onSelectUser: (userId: number) => void;
}) => {
  const currentUser = useGetCurrentUser();
  const showUserSelector =
    !!currentUser && ADMIN_USER_IDS.includes(currentUser.id);

  return (
    <Row className="items-center justify-between gap-2">
      <Row className="items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="h-8" variant="outlineSecondary">
              <FilterIcon className="h-4 w-4" /> Filters
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[200px] p-0">
            <DropdownMenuLabel>Filters</DropdownMenuLabel>
            <DropdownMenuSeparator className="mb-0" />
            <FilterSubMenuMultiselect
              label="Stage"
              options={myCandidaciesStatusOptions}
              selected={myCandidaciesQueryParams.statuses}
              setSelected={(statuses) => {
                handleUpdateView({
                  ...myCandidaciesQueryParams,
                  statuses,
                });
              }}
            />
            <DropdownMenuItem
              className="p-0"
              onClick={() => {
                handleUpdateView({
                  ...myCandidaciesQueryParams,
                  statuses: [],
                });
              }}
            >
              <DropdownMenuLabel className="font-medium">
                Clear all filters
              </DropdownMenuLabel>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="h-8" variant="outlineSecondary">
              <GroupIcon className="h-4 w-4" /> Group by
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="p-0">
            <DropdownMenuLabel>Grouping</DropdownMenuLabel>
            <DropdownMenuSeparator className="mb-0" />
            {groupByOptions.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.label}
                checked={myCandidaciesQueryParams.groupBy === option.value}
                onClick={(e) => {
                  e.preventDefault();
                  handleUpdateView({
                    ...myCandidaciesQueryParams,
                    groupBy:
                      myCandidaciesQueryParams.groupBy === option.value
                        ? undefined
                        : option.value,
                  });
                }}
                className="px-8"
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <TableOrderByDropdown
          queryParams={myCandidaciesQueryParams}
          setQueryParams={handleUpdateView}
          sortOptions={myCandidaciesSortOptions}
        />
      </Row>
      {showUserSelector && (
        <MyAviaryUserSelector
          currentUserId={currentUser.id}
          displayedQueryParams={userSelectorDisplayedQueryParams}
          open={userSelectorOpen}
          pendingSelection={userSelectorPendingSelection}
          onOpenChange={onUserSelectorOpenChange}
          onSelectAll={onSelectAllUsers}
          onSelectUser={onSelectUser}
        />
      )}
    </Row>
  );
};
